/**
 * MapSvgTracer — Dialog for tracing a raster map image into SVG.
 * Supports contour tracing and color region segmentation with live preview.
 */

import { useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Sparkles, Download, Check, RotateCcw, Palette, PenTool,
} from "lucide-react";
import { toast } from "sonner";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/lib/utils";
import {
  traceImageToSvg, suggestTracingMode,
  type TracingMode, type TracingResult,
} from "@/lib/map/svg-tracer";
import { uploadImageToStorage } from "@/lib/world-helpers";

interface MapSvgTracerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceImageUrl: string;
  sourceTitle?: string;
  worldSetting?: string;
  worldId: string;
  onSvgSaved?: (svgUrl: string, svgDataUrl: string) => void;
}

export function MapSvgTracer({
  open, onOpenChange, sourceImageUrl, sourceTitle,
  worldSetting = "", worldId, onSvgSaved,
}: MapSvgTracerProps) {
  const { t } = useLocale();
  const suggestion = suggestTracingMode(worldSetting);

  const [mode, setMode] = useState<TracingMode>(suggestion.mode);
  const [colorCount, setColorCount] = useState(6);
  const [edgeThreshold, setEdgeThreshold] = useState(40);
  const [simplifyTolerance, setSimplifyTolerance] = useState(2);
  const [strokeWidth, setStrokeWidth] = useState(1.5);
  const [tracing, setTracing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<TracingResult | null>(null);

  const handleTrace = useCallback(async () => {
    setTracing(true);
    setResult(null);
    try {
      const res = await traceImageToSvg(sourceImageUrl, {
        mode,
        colorCount,
        edgeThreshold,
        simplifyTolerance,
        strokeWidth,
        strokeColor: "#1a1a2e",
        backgroundColor: mode === "color_region" ? null : undefined,
      });
      setResult(res);
      toast.success(t.svg_traced);
    } catch (err: any) {
      toast.error(err.message || "Tracing failed");
    } finally {
      setTracing(false);
    }
  }, [sourceImageUrl, mode, colorCount, edgeThreshold, simplifyTolerance, strokeWidth]);

  const handleSave = useCallback(async () => {
    if (!result) return;
    setSaving(true);
    try {
      const blob = new Blob([result.svg], { type: "image/svg+xml" });
      const file = new File([blob], `map-traced-${Date.now()}.svg`, { type: "image/svg+xml" });
      const url = await uploadImageToStorage(file, worldId);
      onSvgSaved?.(url, result.dataUrl);
      toast.success(t.svg_lib_saved);
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [result, worldId, onSvgSaved, onOpenChange]);

  const handleDownload = useCallback(() => {
    if (!result) return;
    const blob = new Blob([result.svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `map-traced-${mode}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [result, mode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <PenTool className="w-4 h-4" />
            Trace to SVG
            {sourceTitle && <span className="text-muted-foreground font-normal">— {sourceTitle}</span>}
          </DialogTitle>
        </DialogHeader>

        {/* AI Suggestion */}
        <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground border border-border/30">
          <div className="flex items-center gap-1.5 text-foreground font-medium mb-1">
            <Sparkles className="w-3 h-3 text-primary" />AI Recommendation
          </div>
          {suggestion.reason}
        </div>

        {/* Mode Selection */}
        <div className="grid grid-cols-2 gap-2">
          <button
            className={cn(
              "flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all text-xs",
              mode === "contour"
                ? "border-primary bg-primary/5"
                : "border-border/40 hover:border-border",
            )}
            onClick={() => setMode("contour")}
          >
            <PenTool className="w-5 h-5" />
            <span className="font-medium">Contour Tracing</span>
            <span className="text-muted-foreground text-[10px] text-center">
              Edge outlines — terrain features as vector paths
            </span>
          </button>
          <button
            className={cn(
              "flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all text-xs",
              mode === "color_region"
                ? "border-primary bg-primary/5"
                : "border-border/40 hover:border-border",
            )}
            onClick={() => setMode("color_region")}
          >
            <Palette className="w-5 h-5" />
            <span className="font-medium">Color Regions</span>
            <span className="text-muted-foreground text-[10px] text-center">
              Filled zones — biomes and terrain as colored polygons
            </span>
          </button>
        </div>

        {/* Controls */}
        <div className="space-y-3">
          {mode === "contour" ? (
            <>
              <div>
                <Label className="text-xs">Edge Sensitivity ({edgeThreshold})</Label>
                <Slider
                  value={[edgeThreshold]}
                  onValueChange={([v]) => setEdgeThreshold(v)}
                  min={10} max={100} step={5}
                  className="mt-1"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">Lower = more detail, higher = fewer lines</p>
              </div>
              <div>
                <Label className="text-xs">Stroke Width ({strokeWidth})</Label>
                <Slider
                  value={[strokeWidth * 10]}
                  onValueChange={([v]) => setStrokeWidth(v / 10)}
                  min={5} max={40} step={5}
                  className="mt-1"
                />
              </div>
            </>
          ) : (
            <div>
              <Label className="text-xs">Color Count ({colorCount})</Label>
              <Slider
                value={[colorCount]}
                onValueChange={([v]) => setColorCount(v)}
                min={2} max={16} step={1}
                className="mt-1"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">More colors = more detail, fewer = simpler regions</p>
            </div>
          )}
          <div>
            <Label className="text-xs">Path Simplification ({simplifyTolerance})</Label>
            <Slider
              value={[simplifyTolerance]}
              onValueChange={([v]) => setSimplifyTolerance(v)}
              min={0} max={10} step={0.5}
              className="mt-1"
            />
          </div>
        </div>

        {/* Preview */}
        <div className="grid grid-cols-2 gap-2">
          <div className="border border-border/40 rounded-lg overflow-hidden bg-muted/10">
            <p className="text-[9px] text-muted-foreground px-2 py-1 bg-muted/30">Source</p>
            <img src={sourceImageUrl} alt="Source" className="w-full h-auto" crossOrigin="anonymous" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
          </div>
          <div className="border border-border/40 rounded-lg overflow-hidden bg-muted/10">
            <p className="text-[9px] text-muted-foreground px-2 py-1 bg-muted/30 flex items-center justify-between">
              SVG Result
              {result && <Badge variant="outline" className="text-[8px]">{mode}</Badge>}
            </p>
            {result ? (
              <img src={result.dataUrl} alt="Traced SVG" className="w-full h-auto" />
            ) : (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-xs">
                Click "Trace" to generate
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-row gap-2">
          <Button
            variant="outline" size="sm"
            onClick={handleTrace}
            disabled={tracing}
            className="gap-1.5"
          >
            {tracing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
            {result ? "Re-trace" : "Trace"}
          </Button>
          {result && (
            <>
              <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5">
                <Download className="w-3 h-3" />Download
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="gap-1.5"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Save to Library
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
